import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/motion/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCTS = [
  { value: "vote-estimate", label: "Vote-Estimate" },
  { value: "general", label: "General" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", product: "general", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSent(true);
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", organization: "", product: "general", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-36 bg-muted/50 border-t border-border" data-testid="contact">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.28em] font-semibold text-primary mb-6">
              Get started
            </p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.92]">
              Let’s plan your next
              <span className="text-gradient"> Voting event</span>.
            </h2>
            <p className="mt-8 text-lg text-foreground/70 leading-relaxed max-w-md">
              Tell us about your nation and the voting event ahead. We’ll help you
              choose the right tools and get you moving in days, not months.
            </p>
            <div className="mt-10 space-y-2 text-sm text-foreground/60">
              <p>Prefer email? <span className="text-foreground font-medium">support@smartballot.app</span></p>
              <p>Serving nations coast to coast to coast.</p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Name" htmlFor="name">
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={set("name")}
                    className="bg-transparent border-border focus-visible:ring-primary" placeholder="Your name" />
                </Field>
                <Field label="Email" htmlFor="email">
                  <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")}
                    className="bg-transparent border-border focus-visible:ring-primary" placeholder="you@nation.ca" />
                </Field>
              </div>

              <Field label="Organization / Nation" htmlFor="org">
                <Input id="org" data-testid="contact-org" value={form.organization} onChange={set("organization")}
                  className="bg-transparent border-border focus-visible:ring-primary" placeholder="Optional" />
              </Field>

              <Field label="I'm interested in" htmlFor="product">
                <div className="flex flex-wrap gap-2" data-testid="contact-product">
                  {PRODUCTS.map((p) => (
                    <button
                      type="button"
                      key={p.value}
                      onClick={() => setForm((f) => ({ ...f, product: p.value }))}
                      data-testid={`product-${p.value}`}
                      className={`px-4 py-2 rounded-md text-sm border transition-colors duration-300 ${
                        form.product === p.value
                          ? "bg-foreground text-background border-foreground"
                          : "border-border hover:border-foreground/50"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </Field>

              <Field label="Message" htmlFor="message">
                <Textarea id="message" data-testid="contact-message" value={form.message} onChange={set("message")}
                  rows={5} className="bg-transparent border-border focus-visible:ring-primary resize-none"
                  placeholder="Tell us about the voting event you're planning..." />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-md bg-primary text-primary-foreground font-medium hover:brightness-95 transition-[filter] duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : (sent ? "Send another" : "Send inquiry")}
                {!loading && <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-[0.15em] text-foreground/60">
        {label}
      </Label>
      {children}
    </div>
  );
}
